import { ArrowLeft, ArrowRight, Check, Download, Info, Layers3 } from "lucide-react";

import { getCopy } from "@mobile/app/config/appCopy";

import { WEB_COPY, type AppRoute, type LearnerSetup } from "../appModel";
import type { useWebProgress } from "../progress/webProgress";
import type { ReturnTypeUseWebContentLibrary } from "./contentLibraryViewTypes";
import { domainTitle } from "./domainTitle";
import { ContentHeading, ContentHeadingChoice } from "./ContentHeading";
import type { LearningLevel } from "./webContentTypes";

const LEVEL_LABELS: Record<LearningLevel, string> = {
  a1: "A1",
  a2: "A2",
  b1: "B1",
  b2: "B2",
};

function learnerFacingMetadata(value: string | undefined, title?: string): string | null {
  const normalized = String(value || "").trim();
  if (!normalized || /^[a-z0-9]+(?:_[a-z0-9]+)+$/i.test(normalized)) return null;
  if (normalized.toLocaleLowerCase() === String(title || "").trim().toLocaleLowerCase()) return null;
  return normalized;
}

export function ContentLibraryView(props: {
  setup: LearnerSetup;
  library: ReturnTypeUseWebContentLibrary;
  progressByLevel: Record<LearningLevel, ReturnType<typeof useWebProgress>>;
  requestedMode?: AppRoute | null;
}) {
  const copy = getCopy(props.setup.supportLanguage);
  const webCopy = WEB_COPY[props.setup.supportLanguage];
  const { library } = props;
  const entriesForLevel = library.entries.filter((entry) => entry.level === library.selection.level);
  const selectedEntry = entriesForLevel.find((entry) => entry.domain === library.selection.domain) || null;

  if (library.status === "loading" || library.status === "idle") {
    return <section className="library-state" aria-live="polite"><span className="loading-ring" /><strong>{copy.setupLanguageApplyingTitle}</strong></section>;
  }
  if (library.status === "error") {
    return (
      <section className="library-state" role="alert">
        <strong>{copy.domainContentPromptError}</strong>
        <button type="button" className="secondary-button" onClick={library.retry}>{webCopy.tryAgain}</button>
      </section>
    );
  }

  return (
    <section className="view content-library" aria-labelledby="library-title">
      {library.selection.level ? (
        <div className="library-navigation-row">
          <button type="button" className="back-button library-back" onClick={library.back} aria-label={copy.previous}>
            <ArrowLeft aria-hidden="true" />
          </button>
        </div>
      ) : null}
      {props.requestedMode && props.requestedMode !== "home" && props.requestedMode !== "profile" ? (
        <div className="library-guidance" role="status" tabIndex={-1}>
          <Info aria-hidden="true" />
          <p><strong>{webCopy[props.requestedMode]}</strong><span>{webCopy.chooseScenarioBeforePractice}</span></p>
        </div>
      ) : null}
      {!library.selection.level ? (
        <>
          <div className="view-heading">
            <span>{copy.levelLabel}</span>
            <h1 id="library-title">{copy.setupLevelTitle}</h1>
            <p>{copy.setupLevelHint}</p>
          </div>
          <div className="level-grid">
            {(Object.keys(LEVEL_LABELS) as LearningLevel[]).map((level, index) => {
              const levelEntries = library.entries.filter((entry) => entry.level === level);
              const count = levelEntries
                .reduce((sum, entry) => sum + entry.scenarioCount, 0);
              const scenarioIds = levelEntries.flatMap((entry) => entry.scenarios.map((scenario) => scenario.scenarioId));
              const progressTotal = scenarioIds.reduce((sum, scenarioId) => (
                sum + (props.progressByLevel[level].state.scenarioProgressById[scenarioId] || 0)
              ), 0);
              const progressPercent = scenarioIds.length ? Math.round(progressTotal / scenarioIds.length) : 0;
              return (
                <button key={level} type="button" className={`level-card level-tone-${index + 1}`} onClick={() => library.chooseLevel(level)} disabled={count === 0}>
                  <span>{LEVEL_LABELS[level]}</span>
                  <div><strong>{webCopy.levelNames[level]}</strong><small>{count} {copy.scenarios} · {progressPercent}%</small></div>
                  <ArrowRight aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {library.selection.level && !library.selection.domain ? (
        <>
          <div className="view-heading">
            <span>{library.selection.level.toUpperCase()} · {copy.domainLabel}</span>
            <h1 id="library-title">{webCopy.chooseStudyArea}</h1>
          </div>
          <div className="domain-grid">
            {entriesForLevel.map((entry, index) => {
              const vocabularyCount = entry.scenarios.reduce(
                (sum, scenario) => sum + Math.max(0, Number(scenario.vocabularyCount) || 0),
                0,
              );
              const progressPercent = Math.round(entry.scenarios.reduce(
                (sum, scenario) => sum + (props.progressByLevel[entry.level].state.scenarioProgressById[scenario.scenarioId] || 0),
                0,
              ) / Math.max(1, entry.scenarios.length));
              return (
                <ContentHeadingChoice key={entry.packageId} title={domainTitle(entry.domain, props.setup.targetLanguage)} supportTitle={domainTitle(entry.domain, props.setup.supportLanguage)} setup={props.setup} buttonProps={{ className: "domain-card", onClick: () => library.chooseDomain(entry.domain) }}>
                  {(title, language) => <>
                  <span className={`domain-index tone-${(index % 4) + 1}`}>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <strong lang={language} dir="auto">{title}</strong>
                    <small>{entry.scenarioCount} {copy.scenarios} · {entry.sentenceCount} {copy.sentences} · {vocabularyCount} {copy.modeStatsWords}</small>
                    <em>{copy.progressLabel}: {progressPercent}%</em>
                  </div>
                  <ArrowRight aria-hidden="true" />
                  </>}
                </ContentHeadingChoice>
              );
            })}
          </div>
        </>
      ) : null}

      {selectedEntry ? (
        <>
          <div className="view-heading">
            <span>{library.selection.level?.toUpperCase()} · <ContentHeading title={domainTitle(selectedEntry.domain, props.setup.targetLanguage)} supportTitle={domainTitle(selectedEntry.domain, props.setup.supportLanguage)} setup={props.setup} /></span>
            <h1 id="library-title">{copy.pickScenarioTitle}</h1>
            <p>{copy.pickScenarioHint}</p>
          </div>
          <div className="scenario-grid">
            {selectedEntry.scenarios.map((scenario, index) => {
              const supportScenario = selectedEntry.supportScenariosByUiLanguage?.[props.setup.supportLanguage]
                ?.find((item) => item.scenarioId === scenario.scenarioId);
              const microSkill = learnerFacingMetadata(scenario.microSkill, scenario.title);
              const scenarioProgress = props.progressByLevel[selectedEntry.level].state.scenarioProgressById[scenario.scenarioId] || 0;
              return (
                <ContentHeadingChoice key={scenario.scenarioId} title={scenario.title} supportTitle={supportScenario?.title} setup={props.setup} buttonProps={{ className: "scenario-card", onClick: () => void library.openScenario(selectedEntry, scenario.scenarioId) }}>
                  {(title, language) => <>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <strong lang={language} dir="auto">{title}</strong>
                    <small>
                      {scenario.sentenceCount || 0} {copy.sentences} · {scenario.vocabularyCount || 0} {copy.modeStatsWords} · {scenario.missionCount || 0} {copy.modeStatsMissions}
                      {microSkill ? ` · ${microSkill}` : ""}
                    </small>
                    <em>{copy.progressLabel}: {scenarioProgress}%</em>
                  </div>
                  <ArrowRight aria-hidden="true" />
                  </>}
                </ContentHeadingChoice>
              );
            })}
          </div>
        </>
      ) : null}

      {library.pendingScenario ? (
        <div className="dialog-backdrop" role="presentation">
          <section className="content-package-dialog" role="dialog" aria-modal="true" aria-labelledby="package-title">
            <span className="package-icon" aria-hidden="true"><Layers3 /></span>
            <div><span>{library.pendingScenario.entry.level.toUpperCase()}</span><h2 id="package-title">{copy.domainContentPromptTitle}</h2></div>
            <p>{copy.domainContentPromptQuestion}</p>
            <div className="package-summary">
              <strong><ContentHeading title={domainTitle(library.pendingScenario.entry.domain, props.setup.targetLanguage)} supportTitle={domainTitle(library.pendingScenario.entry.domain, props.setup.supportLanguage)} setup={props.setup} /></strong>
              <span>{library.pendingScenario.entry.scenarioCount} {copy.scenarios}</span>
              <span>{library.pendingScenario.entry.estimatedSizeMb.toFixed(2)} MB</span>
            </div>
            {library.installing ? (
              <div className="package-progress" aria-live="polite">
                <span className="loading-ring" aria-hidden="true" />
                <strong>{copy.domainContentPromptInstalling}</strong>
                <div><span style={{ width: `${library.installProgress || 18}%` }} /></div>
              </div>
            ) : null}
            {library.error ? <p className="error-status">{copy.domainContentPromptError}</p> : null}
            <div className="dialog-actions">
              <button type="button" className="secondary-button" onClick={library.closeInstall}>{copy.cancelLabel}</button>
              <button type="button" className="primary-button" onClick={() => void library.installPending()} disabled={library.installing}>
                {library.installing ? <span className="loading-ring" aria-hidden="true" /> : <Download aria-hidden="true" />}
                {library.installing ? copy.domainContentPromptInstalling : copy.domainContentPromptInstall}
              </button>
            </div>
            {library.installProgress === 100 ? <Check aria-hidden="true" /> : null}
          </section>
        </div>
      ) : null}
    </section>
  );
}
